import { DateTime } from "luxon"
import { Dinero, multiply } from "dinero.js"
import { RepeatOrderFormValues } from "../../../createOrder/components/drawer/meetingDetails/RepeatOrderDialog"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"

export function getRecurringDates(
    dateTimeFactory: DateTimeFactory,
    values: Partial<RepeatOrderFormValues>,
): DateTime[] {
    if (!values.startDate || !values.repeatType) return []

    const dates: DateTime[] = []
    const interval = values.repeatInterval || 1
    const type = values.repeatType
    const weekDays = values.weekDays ?? []

    let date = values.startDate.startOf("day")

    const useCount = values.endType === "after" && values.repeatCount
    const useEndDate = values.endType === "on" && values.endDate

    while (true) {
        if (useCount && dates.length >= values.repeatCount!) break
        if (useEndDate && date > values.endDate!.endOf("day")) break

        if (type === "day") {
            dates.push(date)
            date = date.plus({ days: interval })
        } else if (type === "week" && weekDays.length > 0) {
            const weekStart = date.startOf("week") // Monday
            const weekDates = weekDays
                .map((dayName) => {
                    const dayIndex = dateTimeFactory.fromHourMinute(dayName, "EEEE").weekday
                    return weekStart.plus({ days: dayIndex - 1 })
                })
                .filter((d) => d >= date && (!useEndDate || d <= values.endDate!.endOf("day")))

            for (const d of weekDates) {
                if (useCount && dates.length >= values.repeatCount!) break
                dates.push(d)
            }

            date = weekStart.plus({ weeks: interval })
        } else {
            break
        }
    }

    return dates
}

export function groupDatesByMonth(dates: DateTime[]): Record<string, string[]> {
    return dates.reduce(
        (acc, dt) => {
            const key = dt.toFormat("MMMM yyyy")
            const formatted = dt.toFormat("d")
            if (!acc[key]) acc[key] = []
            acc[key].push(formatted)
            return acc
        },
        {} as Record<string, string[]>,
    )
}
type DateRange = { start: string; end: string } | null

export function formattedMeetingTimeRange(
    dateTimeFactory: DateTimeFactory,
    start?: string | null,
    end?: string | null,
): string {
    if (!start || !end) return ""

    const startDateTime = dateTimeFactory.fromApi(start)
    const endDateTime = dateTimeFactory.fromApi(end)

    return dateTimeFactory.formatPeriod(startDateTime, endDateTime)
}

function hasTimePart(value: string): boolean {
    return /\d{2}:\d{2}/.test(value)
}

export function formatDeliveryDateForReceipt(
    dateTimeFactory: DateTimeFactory,
    {
        deliveryDateValue,
        firstDeliveryTimeValue,
    }: Readonly<{
        deliveryDateValue: string | null
        firstDeliveryTimeValue: string | null
    }>,
): string | null {
    if (!deliveryDateValue) return null

    const deliveryDateTimeValue = hasTimePart(deliveryDateValue)
        ? deliveryDateValue
        : firstDeliveryTimeValue
          ? `${deliveryDateValue} ${firstDeliveryTimeValue}`
          : deliveryDateValue

    const dt = dateTimeFactory.fromApi(deliveryDateTimeValue)
    if (!dt.isValid) return null

    return hasTimePart(deliveryDateTimeValue) ? dateTimeFactory.formatDateTime(dt) : dateTimeFactory.formatDate(dt)
}

export function calculateVATDinero(total: Dinero<number>, vatRate: number, priceIncludesVat: boolean) {
    // When price includes VAT: extract VAT component using vatRate / (100 + vatRate)
    // When price excludes VAT: calculate VAT using vatRate / 100
    const vatRatio = priceIncludesVat ? vatRate / (100 + vatRate) : vatRate / 100
    const [integer, decimals = ""] = vatRatio.toString().split(".")

    const ratio = {
        amount: parseInt(integer + decimals, 10),
        scale: decimals.length,
    }

    return multiply(total, ratio)
}
